import { useState, useMemo, useCallback } from 'react';
import { Navigation } from '../components/Navigation';
import { EarthScene, computeOrbitPoints } from '../components/EarthScene';
import { ORBITS, ORBIT_ORDER } from '../data/orbits';
import { useProgress } from '../hooks/useProgress';
import { RotateCcw, Eye, EyeOff, Satellite, ArrowRightLeft, ChevronDown, Edit3, Check, Focus, Crown, Wrench, Radio, MapPin } from 'lucide-react';
import { TLEParser } from '../components/TLEParser';
import { PremiumModal } from '../components/PremiumModal';

const EXPLORE_ORBIT_ORDER = ORBIT_ORDER.filter(id => id !== 'orbital_elements' && id !== 'hohmann');

const GPS_CONSTELLATION = [];
for (let plane = 0; plane < 6; plane++) {
  for (let sat = 0; sat < 4; sat++) {
    GPS_CONSTELLATION.push({
      semiMajor: 4.8, eccentricity: 0.01, inclination: 55,
      raan: plane * 60, color: '#00FF94', phase: (sat / 4) + (plane * 0.05),
    });
  }
}

const PARAM_DEFAULTS = {
  semiMajor: 3.0, eccentricity: 0.1, inclination: 45,
  raan: 0, color: '#00F0FF', speed: 0.12,
};

function SliderParam({ label, value, min, max, step, unit, onChange, color }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-1.5">
        <span style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.15em', textTransform: 'uppercase' }}>{label}</span>
        <span style={{ fontFamily: 'JetBrains Mono', fontSize: '13px', color: color || '#00F0FF', fontWeight: 500 }}>
          {typeof value === 'number' ? value.toFixed(step < 0.1 ? 2 : 1) : value}{unit}
        </span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        className="w-full h-1 rounded-full appearance-none cursor-pointer"
        style={{ accentColor: color || '#00F0FF', background: `linear-gradient(to right, ${color || '#00F0FF'} ${pct}%, rgba(255,255,255,0.1) ${pct}%)` }}
        data-testid={`slider-${label.toLowerCase().replace(/\s/g, '-')}`} />
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, color = '#00F0FF' }) {
  return (
    <button onClick={onClick} data-testid={`tab-${label.toLowerCase()}`}
      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg transition-all"
      style={{
        background: active ? `${color}12` : 'transparent',
        border: `1px solid ${active ? `${color}40` : 'rgba(255,255,255,0.06)'}`,
        cursor: 'pointer', fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '12px',
        color: active ? color : 'rgba(255,255,255,0.4)',
        textTransform: 'uppercase', letterSpacing: '0.08em',
      }}>
      <Icon size={13} />{label}
    </button>
  );
}

export default function Explore() {
  const { isOrbitUnlocked, isFeatureUnlocked, progress } = useProgress();
  const [params, setParams] = useState(PARAM_DEFAULTS);
  const [selectedPresetId, setSelectedPresetId] = useState(null);
  const [showLabels, setShowLabels] = useState(false);
  const [showGPS, setShowGPS] = useState(false);
  const [overlayIds, setOverlayIds] = useState([]);
  const [hohmannMode, setHohmannMode] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [hohmannPhase, setHohmannPhase] = useState(0);
  const [followSatellite, setFollowSatellite] = useState(null);
  const [tleOrbit, setTleOrbit] = useState(null);
  const [realTimeSat, setRealTimeSat] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [activeTab, setActiveTab] = useState('build');
  const [overheadSatellites, setOverheadSatellites] = useState(null);

  const toggleOverlay = (id) => setOverlayIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const overlayOrbits = useMemo(() => overlayIds.map(id => ORBITS[id]).filter(Boolean), [overlayIds]);
  const handlePhaseChange = useCallback((p) => setHohmannPhase(p), []);

  const hohmannOrbits = useMemo(() => {
    if (!hohmannMode) return [];
    const r1 = 2.45, r2 = 7.8, transferSMA = (r1 + r2) / 2, transferEcc = (r2 - r1) / (r2 + r1);
    return [
      { ...ORBITS.leo, id: 'h-start', shortName: 'LEO', color: '#00FF94', speed: 0.15, noSatellite: true },
      { id: 'h-transfer', name: 'Transfer Orbit', shortName: 'TRANSFER', color: '#E879F9', semiMajor: transferSMA, eccentricity: transferEcc, inclination: 0.5, raan: 0, speed: 0.04, noSatellite: true },
      { ...ORBITS.geo, id: 'h-end', shortName: 'GEO', color: '#FFD700', speed: 0.04, noSatellite: true },
    ];
  }, [hohmannMode]);

  const transferAnimConfig = useMemo(() => {
    if (!hohmannMode) return null;
    const r1 = 2.45, r2 = 7.8;
    return {
      startOrbit: { semiMajor: r1, eccentricity: 0.001, inclination: 0.5, raan: 0, speed: 0.15 },
      transferOrbit: { semiMajor: (r1 + r2) / 2, eccentricity: (r2 - r1) / (r2 + r1), inclination: 0.5, raan: 0, speed: 0.04 },
      endOrbit: { semiMajor: r2, eccentricity: 0.0, inclination: 0.5, raan: 0, speed: 0.04 },
      onPhaseChange: handlePhaseChange,
    };
  }, [hohmannMode, handlePhaseChange]);

  const customOrbit = useMemo(() => ({
    id: 'custom',
    name: selectedPresetId ? (ORBITS[selectedPresetId]?.name || 'Custom Orbit') : 'Custom Orbit',
    shortName: selectedPresetId ? (ORBITS[selectedPresetId]?.shortName || 'CUSTOM') : 'CUSTOM',
    color: params.color, semiMajor: params.semiMajor, eccentricity: params.eccentricity,
    inclination: params.inclination, raan: params.raan, speed: params.speed,
  }), [params, selectedPresetId]);

  const perigeeFactor = params.semiMajor * (1 - params.eccentricity);
  const apogeeFactor = params.semiMajor * (1 + params.eccentricity);
  const periKm = Math.max(200, Math.round(((perigeeFactor - 2.0) / 6.0) * 33000));
  const apoKm = Math.round(((apogeeFactor - 2.0) / 6.0) * 33000);
  const periodMin = Math.round(84.3 * Math.pow((params.semiMajor / 2.0), 1.5));

  const loadPreset = (id) => {
    const o = ORBITS[id];
    if (!o) return;
    setParams({ semiMajor: o.semiMajor, eccentricity: o.eccentricity, inclination: o.inclination, raan: o.raan, color: o.color, speed: o.speed });
    setSelectedPresetId(id);
  };

  const reset = () => { setParams(PARAM_DEFAULTS); setSelectedPresetId(null); };

  return (
    <div style={{ height: '100vh', background: '#030305', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Navigation />
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', marginTop: '64px' }}>
        {/* 3D Scene */}
        <div style={{ flex: 1, position: 'relative' }}>
          <EarthScene
            activeOrbits={hohmannMode ? hohmannOrbits : (activeTab === 'track' ? [] : [customOrbit, ...overlayOrbits])}
            selectedOrbit={hohmannMode ? null : (activeTab === 'track' ? null : customOrbit)}
            height="100%" interactive={true}
            showLabels={showLabels || hohmannMode}
            constellationOrbits={showGPS ? GPS_CONSTELLATION : null}
            transferAnimation={transferAnimConfig}
            followSatellite={followSatellite}
            userLocation={userLocation}
            realTimeSatellite={realTimeSat}
            overheadSatellites={overheadSatellites}
          />

          {/* Follow indicator */}
          {followSatellite && (
            <div style={{ position: 'absolute', top: '16px', left: '50%', transform: 'translateX(-50%)', zIndex: 10, display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: 'rgba(232,121,249,0.15)', border: '1px solid rgba(232,121,249,0.4)', borderRadius: '20px', backdropFilter: 'blur(8px)' }}>
              <Focus size={14} color="#E879F9" />
              <span style={{ fontFamily: 'JetBrains Mono', fontSize: '11px', color: '#E879F9', letterSpacing: '0.1em' }}>FOLLOWING SATELLITE</span>
              <button onClick={() => setFollowSatellite(null)}
                style={{ marginLeft: '8px', padding: '2px 8px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '4px', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontFamily: 'JetBrains Mono', fontSize: '10px' }}>EXIT</button>
            </div>
          )}

          {/* Hohmann phase */}
          {hohmannMode && (
            <div data-testid="hohmann-phase-indicator" style={{ position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 10, display: 'flex', gap: '4px', alignItems: 'center', padding: '8px 14px', background: 'rgba(0,0,0,0.75)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', backdropFilter: 'blur(8px)' }}>
              {['LEO ORBIT', 'BURN +2.46 km/s', 'TRANSFER COAST', 'BURN +1.47 km/s', 'GEO ORBIT'].map((label, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {i > 0 && <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '10px' }}>{'>'}</span>}
                  <span style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', letterSpacing: '0.06em', padding: '2px 6px', borderRadius: '4px',
                    color: hohmannPhase === i ? (i === 1 || i === 3 ? '#FFCC00' : '#00F0FF') : 'rgba(255,255,255,0.3)',
                    background: hohmannPhase === i ? (i === 1 || i === 3 ? 'rgba(255,200,0,0.15)' : 'rgba(0,240,255,0.1)') : 'transparent',
                    border: hohmannPhase === i ? `1px solid ${i === 1 || i === 3 ? 'rgba(255,200,0,0.3)' : 'rgba(0,240,255,0.2)'}` : '1px solid transparent',
                    transition: 'all 0.3s',
                  }}>{label}</span>
                </div>
              ))}
            </div>
          )}

          <div style={{ position: 'absolute', top: '16px', left: '16px', fontFamily: 'JetBrains Mono', fontSize: '11px', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.12em' }}>
            DRAG TO ROTATE &bull; SCROLL TO ZOOM
          </div>
        </div>

        {/* Control Panel */}
        <div style={{ width: '340px', flexShrink: 0, display: 'flex', flexDirection: 'column', background: 'rgba(11,16,21,0.95)', borderLeft: '1px solid rgba(255,255,255,0.07)' }} data-testid="control-panel">

          {/* Tabs */}
          <div className="flex gap-2 p-3 pb-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <TabButton active={activeTab === 'build'} onClick={() => setActiveTab('build')} icon={Wrench} label="Build" color="#00F0FF" />
            <TabButton active={activeTab === 'track'} onClick={() => setActiveTab('track')} icon={Radio} label="Track" color="#E879F9" />
            <TabButton active={activeTab === 'tools'} onClick={() => setActiveTab('tools')} icon={Satellite} label="Tools" color="#00FF94" />
          </div>

          {/* Tab Content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>

            {/* ═══ BUILD TAB ═══ */}
            {activeTab === 'build' && (
              <div className="animate-fadeIn">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.2em' }}>ORBIT BUILDER</p>
                    <h2 style={{ fontFamily: 'Rajdhani', fontWeight: 700, fontSize: '18px', color: '#fff', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Parameters</h2>
                  </div>
                  <button onClick={reset} className="p-2 rounded-lg flex items-center gap-1" data-testid="reset-params-btn"
                    style={{ color: 'rgba(255,255,255,0.4)', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', cursor: 'pointer', fontSize: '11px', fontFamily: 'Rajdhani', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                    <RotateCcw size={12} /> Reset
                  </button>
                </div>

                {/* Presets */}
                <div className="mb-4">
                  <p style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: '#00F0FF', letterSpacing: '0.2em', marginBottom: '8px' }}>PRESETS</p>
                  <div className="flex flex-wrap gap-1.5">
                    {EXPLORE_ORBIT_ORDER.map(id => {
                      const o = ORBITS[id];
                      const unlocked = isOrbitUnlocked(id);
                      return (
                        <button key={id} onClick={() => unlocked && loadPreset(id)} disabled={!unlocked}
                          data-testid={`preset-${id}`}
                          style={{ padding: '3px 9px', borderRadius: '9999px', fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '11px',
                            textTransform: 'uppercase', letterSpacing: '0.04em',
                            color: selectedPresetId === id ? '#000' : unlocked ? o.color : 'rgba(255,255,255,0.2)',
                            background: selectedPresetId === id ? o.color : `${o.color}12`,
                            border: `1px solid ${unlocked ? o.color + '40' : 'rgba(255,255,255,0.1)'}`,
                            cursor: unlocked ? 'pointer' : 'not-allowed', opacity: unlocked ? 1 : 0.5, transition: 'all 0.2s' }}>
                          {o.shortName}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Color */}
                <div className="mb-4">
                  <p style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.15em', marginBottom: '6px' }}>COLOR</p>
                  <div className="flex gap-2 flex-wrap">
                    {['#00F0FF', '#00FF94', '#FFD700', '#FF3B30', '#E879F9', '#FB923C', '#60A5FA'].map(c => (
                      <button key={c} onClick={() => setParams(p => ({ ...p, color: c }))} data-testid={`color-${c.slice(1)}`}
                        style={{ width: '22px', height: '22px', borderRadius: '50%', background: c, border: params.color === c ? '2px solid #fff' : '2px solid transparent', cursor: 'pointer', flexShrink: 0 }} />
                    ))}
                  </div>
                </div>

                {/* Sliders */}
                <SliderParam label="Semi-Major Axis" value={params.semiMajor} min={2.2} max={10} step={0.1} unit=" u" onChange={v => setParams(p => ({ ...p, semiMajor: v }))} color={params.color} />
                <SliderParam label="Eccentricity" value={params.eccentricity} min={0} max={0.92} step={0.01} unit="" onChange={v => setParams(p => ({ ...p, eccentricity: v }))} color={params.color} />
                <SliderParam label="Inclination" value={params.inclination} min={0} max={180} step={0.5} unit="°" onChange={v => setParams(p => ({ ...p, inclination: v }))} color={params.color} />

                {/* Advanced */}
                <button onClick={() => setShowAdvanced(v => !v)} data-testid="toggle-advanced"
                  className="flex items-center gap-2 w-full mb-3"
                  style={{ cursor: 'pointer', background: 'none', border: 'none', padding: '4px 0', color: 'rgba(255,255,255,0.35)', fontFamily: 'JetBrains Mono', fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                  <ChevronDown size={12} style={{ transform: showAdvanced ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
                  {showAdvanced ? 'HIDE' : 'SHOW'} ADVANCED
                </button>
                {showAdvanced && (
                  <>
                    <SliderParam label="RAAN" value={params.raan} min={0} max={360} step={1} unit="°" onChange={v => setParams(p => ({ ...p, raan: v }))} color={params.color} />
                    <SliderParam label="Speed" value={params.speed} min={0.01} max={0.4} step={0.01} unit="x" onChange={v => setParams(p => ({ ...p, speed: v }))} color={params.color} />
                  </>
                )}

                {/* Orbital Data Table */}
                <div className="rounded-lg overflow-hidden mb-4" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
                  {[
                    ['TYPE', params.eccentricity < 0.05 ? 'Circular' : params.eccentricity < 0.4 ? 'Near-Circular' : 'Elliptical'],
                    ['PERIGEE', `~${periKm.toLocaleString()} km`],
                    ['APOGEE', `~${apoKm.toLocaleString()} km`],
                    ['PERIOD', `~${periodMin > 1440 ? (periodMin / 1440).toFixed(1) + ' days' : periodMin + ' min'}`],
                    ['INCLINATION', `${params.inclination.toFixed(1)}°`],
                  ].map(([label, val], i, arr) => (
                    <div key={label} className="flex justify-between px-3 py-1.5"
                      style={{ background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent', borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                      <span style={{ fontFamily: 'JetBrains Mono', fontSize: '9px', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.1em' }}>{label}</span>
                      <span style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: params.color }}>{val}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ═══ TRACK TAB ═══ */}
            {activeTab === 'track' && (
              <div className="animate-fadeIn">
                <TLEParser
                  onOrbitParsed={setTleOrbit}
                  onRealTimeSatellite={setRealTimeSat}
                  onLocationSet={setUserLocation}
                  onOverheadSatellites={setOverheadSatellites}
                  isPremium={isFeatureUnlocked('tle_parser')}
                />
              </div>
            )}

            {/* ═══ TOOLS TAB ═══ */}
            {activeTab === 'tools' && (
              <div className="animate-fadeIn">
                {/* Visualization Toggles */}
                <div className="mb-5">
                  <p style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.15em', marginBottom: '8px' }}>VISUALIZATION</p>
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => setShowLabels(v => !v)} data-testid="toggle-labels"
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
                      style={{ background: showLabels ? 'rgba(0,240,255,0.1)' : 'rgba(255,255,255,0.03)', border: `1px solid ${showLabels ? 'rgba(0,240,255,0.3)' : 'rgba(255,255,255,0.08)'}`, cursor: 'pointer' }}>
                      {showLabels ? <Eye size={12} color="#00F0FF" /> : <EyeOff size={12} color="rgba(255,255,255,0.3)" />}
                      <span style={{ fontFamily: 'Rajdhani', fontSize: '11px', fontWeight: 600, color: showLabels ? '#00F0FF' : 'rgba(255,255,255,0.5)', textTransform: 'uppercase' }}>Labels</span>
                    </button>
                    <button onClick={() => setShowGPS(v => !v)} data-testid="toggle-gps"
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
                      style={{ background: showGPS ? 'rgba(0,255,148,0.1)' : 'rgba(255,255,255,0.03)', border: `1px solid ${showGPS ? 'rgba(0,255,148,0.3)' : 'rgba(255,255,255,0.08)'}`, cursor: 'pointer' }}>
                      <Satellite size={12} color={showGPS ? '#00FF94' : 'rgba(255,255,255,0.3)'} />
                      <span style={{ fontFamily: 'Rajdhani', fontSize: '11px', fontWeight: 600, color: showGPS ? '#00FF94' : 'rgba(255,255,255,0.5)', textTransform: 'uppercase' }}>GPS</span>
                    </button>
                    <button onClick={() => { setHohmannMode(v => !v); if (!hohmannMode) setOverlayIds([]); }} data-testid="toggle-hohmann"
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
                      style={{ background: hohmannMode ? 'rgba(232,121,249,0.1)' : 'rgba(255,255,255,0.03)', border: `1px solid ${hohmannMode ? 'rgba(232,121,249,0.3)' : 'rgba(255,255,255,0.08)'}`, cursor: 'pointer' }}>
                      <ArrowRightLeft size={12} color={hohmannMode ? '#E879F9' : 'rgba(255,255,255,0.3)'} />
                      <span style={{ fontFamily: 'Rajdhani', fontSize: '11px', fontWeight: 600, color: hohmannMode ? '#E879F9' : 'rgba(255,255,255,0.5)', textTransform: 'uppercase' }}>Hohmann</span>
                    </button>
                  </div>
                </div>

                {/* Compare Orbits */}
                {!hohmannMode && (
                  <div className="mb-5">
                    <p style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.15em', marginBottom: '8px' }}>COMPARE ORBITS</p>
                    <div className="flex flex-wrap gap-1.5">
                      {EXPLORE_ORBIT_ORDER.map(id => {
                        const o = ORBITS[id];
                        const active = overlayIds.includes(id);
                        return (
                          <button key={id} onClick={() => toggleOverlay(id)} data-testid={`overlay-${id}`}
                            style={{ padding: '2px 8px', borderRadius: '9999px', fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '11px',
                              textTransform: 'uppercase', letterSpacing: '0.04em', cursor: 'pointer', transition: 'all 0.2s',
                              color: active ? '#000' : o.color, background: active ? o.color : `${o.color}12`,
                              border: `1px solid ${o.color}${active ? '' : '40'}` }}>
                            {o.shortName}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Camera Lock */}
                <div className="mb-5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Focus size={14} color={isFeatureUnlocked('camera_lock') ? '#E879F9' : 'rgba(255,255,255,0.3)'} />
                      <span style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: isFeatureUnlocked('camera_lock') ? '#E879F9' : 'rgba(255,255,255,0.4)', letterSpacing: '0.15em' }}>CAMERA LOCK</span>
                    </div>
                    {!isFeatureUnlocked('camera_lock') && (
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs" style={{ background: 'rgba(255,215,0,0.15)', color: '#FFD700', fontFamily: 'Rajdhani', fontWeight: 600 }}>
                        <Crown size={10} /> PREMIUM
                      </span>
                    )}
                  </div>
                  {isFeatureUnlocked('camera_lock') ? (
                    <div className="space-y-1.5">
                      <button onClick={() => setFollowSatellite(followSatellite === 'custom' ? null : 'custom')} data-testid="follow-custom"
                        className="w-full py-1.5 px-3 rounded-lg flex items-center justify-between"
                        style={{ background: followSatellite === 'custom' ? 'rgba(232,121,249,0.2)' : 'rgba(255,255,255,0.03)', border: `1px solid ${followSatellite === 'custom' ? 'rgba(232,121,249,0.4)' : 'rgba(255,255,255,0.08)'}`, cursor: 'pointer' }}>
                        <div className="flex items-center gap-2">
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: params.color }} />
                          <span style={{ fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '12px', color: followSatellite === 'custom' ? '#E879F9' : 'rgba(255,255,255,0.6)', textTransform: 'uppercase' }}>{customOrbit.shortName}</span>
                        </div>
                        {followSatellite === 'custom' && <Focus size={12} color="#E879F9" className="animate-pulse" />}
                      </button>
                      {overlayOrbits.map(o => (
                        <button key={o.id} onClick={() => setFollowSatellite(followSatellite === o.id ? null : o.id)} data-testid={`follow-${o.id}`}
                          className="w-full py-1.5 px-3 rounded-lg flex items-center justify-between"
                          style={{ background: followSatellite === o.id ? 'rgba(232,121,249,0.2)' : 'rgba(255,255,255,0.03)', border: `1px solid ${followSatellite === o.id ? 'rgba(232,121,249,0.4)' : 'rgba(255,255,255,0.08)'}`, cursor: 'pointer' }}>
                          <div className="flex items-center gap-2">
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: o.color }} />
                            <span style={{ fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '12px', color: followSatellite === o.id ? '#E879F9' : 'rgba(255,255,255,0.6)', textTransform: 'uppercase' }}>{o.shortName}</span>
                          </div>
                          {followSatellite === o.id && <Focus size={12} color="#E879F9" className="animate-pulse" />}
                        </button>
                      ))}
                      {/* TLE real-time satellite */}
                      {realTimeSat && (
                        <button onClick={() => setFollowSatellite(followSatellite === 'tle_realtime' ? null : 'tle_realtime')} data-testid="follow-tle"
                          className="w-full py-1.5 px-3 rounded-lg flex items-center justify-between"
                          style={{ background: followSatellite === 'tle_realtime' ? 'rgba(232,121,249,0.2)' : 'rgba(255,255,255,0.03)', border: `1px solid ${followSatellite === 'tle_realtime' ? 'rgba(232,121,249,0.4)' : 'rgba(255,255,255,0.08)'}`, cursor: 'pointer' }}>
                          <div className="flex items-center gap-2">
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#FF3B30' }} />
                            <span style={{ fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '12px', color: followSatellite === 'tle_realtime' ? '#E879F9' : 'rgba(255,255,255,0.6)', textTransform: 'uppercase' }}>{realTimeSat.name || 'TLE'}</span>
                          </div>
                          {followSatellite === 'tle_realtime' && <Focus size={12} color="#E879F9" className="animate-pulse" />}
                        </button>
                      )}
                      {followSatellite && (
                        <button onClick={() => setFollowSatellite(null)} data-testid="exit-follow-mode"
                          className="w-full py-1.5 rounded-lg flex items-center justify-center gap-2 mt-1"
                          style={{ background: 'rgba(255,68,68,0.1)', border: '1px solid rgba(255,68,68,0.3)', cursor: 'pointer', fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '11px', color: '#FF4444', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                          Exit Follow Mode
                        </button>
                      )}
                    </div>
                  ) : (
                    <button onClick={() => setShowPremiumModal(true)}
                      className="w-full py-2 rounded-lg flex items-center justify-center gap-2"
                      style={{ background: 'rgba(255,215,0,0.05)', border: '1px solid rgba(255,215,0,0.2)', cursor: 'pointer', fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '12px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>
                      <Crown size={14} color="#FFD700" /> Unlock Camera Lock
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <PremiumModal isOpen={showPremiumModal} onClose={() => setShowPremiumModal(false)} />
    </div>
  );
}
